import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useTeam } from '../contexts/TeamContext';
import { API_URL } from '../services/api';
import { useApiHeaders } from '../hooks/useApiHeaders';
import LogViewer from '../components/LogViewer';
import LicenseExpiredBanner from '../components/LicenseExpiredBanner';
import LimitWarning from '../components/LimitWarning';
import PlatformInstallGuide, { type TokenError } from '../components/PlatformInstallGuide';

interface Agent {
	agent_id: string;
	hostname: string;
	platform: string;
	version: string;
	status: 'online' | 'offline';
	last_heartbeat: number;
	created_at: number;
	latest_version?: string;
	update_available?: boolean;
}

interface BotInstance {
	instance_id: string;
	agent_id: string;
	clawdbot_version: string;
	install_path: string;
	running_status: 'running' | 'stopped' | 'error';
	agent_hostname?: string;
	status_warning?: string | null;
}

type SetupStep = 'no-agent' | 'agent-deploying' | 'agent-ready' | 'managing-bots';

export default function InstancesPage() {
	const { firebaseUser } = useAuth();
	const { currentTeam } = useTeam();
	const { getHeaders } = useApiHeaders();
	const [currentStep, setCurrentStep] = useState<SetupStep>('no-agent');
	const [agents, setAgents] = useState<Agent[]>([]);
	const [selectedAgent, setSelectedAgent] = useState<Agent | null>(null);
	const [bots, setBots] = useState<BotInstance[]>([]);
	const [discovering, setDiscovering] = useState(false);
	const [actionLoading, setActionLoading] = useState<string | null>(null);
	const [registrationToken, setRegistrationToken] = useState<string | null>(null);
	const [tokenLoading, setTokenLoading] = useState(true);
	const [tokenError, setTokenError] = useState<TokenError | null>(null);
	const [activeCommandId, setActiveCommandId] = useState<string | null>(null);
	const [showLogs, setShowLogs] = useState(false);
	const [showCleanConfirm, setShowCleanConfirm] = useState<string | null>(null); // agent_id for pending clean install
	const [showUninstallConfirm, setShowUninstallConfirm] = useState<string | null>(null); // instance_id for pending uninstall

	// Fetch registration token from API
	useEffect(() => {
		let cancelled = false;

		const fetchToken = async () => {
			console.log('[Instances] fetchToken called, firebaseUser:', !!firebaseUser);
			if (!firebaseUser) {
				console.log('[Instances] No firebaseUser, skipping token fetch');
				setTokenLoading(false);
				return;
			}

			try {
				const headers = await getHeaders();
				// Use relative URL to leverage Vite proxy in development
				const tokenUrl = `${API_URL}/api/v1/agents/token?server_url=${encodeURIComponent(API_URL)}`;
				console.log('[Instances] Fetching token from:', tokenUrl);
				const response = await fetch(tokenUrl, {
					headers: headers,
				});

				if (cancelled) return;

				console.log('[Instances] Response status:', response.status);
				if (response.ok) {
					const data = await response.json();
					console.log('[Instances] Token received:', data.token);
					setRegistrationToken(data.token);
					setTokenError(null);
				} else if (response.status === 403) {
					const errorData = await response.json().catch(() => ({ error: 'Forbidden', message: 'Access denied' }));
					console.error('[Instances] License error:', errorData);
					setTokenError(errorData);
				} else {
					const errorText = await response.text();
					console.error('[Instances] Failed to fetch registration token:', response.status, errorText);
				}
			} catch (error) {
				if (!cancelled) {
					console.error('[Instances] Error fetching registration token:', error);
				}
			} finally {
				if (!cancelled) {
					setTokenLoading(false);
				}
			}
		};

		fetchToken();

		return () => {
			cancelled = true;
		};
	}, [firebaseUser, currentTeam]);

	// Fetch agents from API
	useEffect(() => {
		let cancelled = false;

		const fetchAgents = async () => {
			if (!firebaseUser) return;

			try {
				const headers = await getHeaders();
				const response = await fetch(`${API_URL}/api/v1/agents`, {
					headers: headers,
				});

				if (cancelled) return;

				if (response.ok) {
					const data = await response.json();
					const agentList = data.agents || [];
					setAgents(agentList);
					if (agentList.length > 0) {
						setCurrentStep('agent-ready');
					}
				}
			} catch (error) {
				if (!cancelled) {
					console.error('Error fetching agents:', error);
				}
			}
		};

		fetchAgents();
		const interval = setInterval(fetchAgents, 30000);

		return () => {
			cancelled = true;
			clearInterval(interval);
		};
	}, [firebaseUser, currentTeam]);

	// Fetch bot instances from API
	useEffect(() => {
		let cancelled = false;

		const fetchInstances = async () => {
			if (!firebaseUser) return;

			try {
				const headers = await getHeaders();
				const response = await fetch(`${API_URL}/api/v1/instances`, {
					headers: headers,
				});

				if (cancelled) return;

				if (response.ok) {
					const data = await response.json();
					setBots(data.instances || []);
					if ((data.instances || []).length > 0) {
						setCurrentStep('managing-bots');
					}
				}
			} catch (error) {
				if (!cancelled) {
					console.error('Error fetching instances:', error);
				}
			}
		};

		fetchInstances();
		const interval = setInterval(fetchInstances, 30000);

		return () => {
			cancelled = true;
			clearInterval(interval);
		};
	}, [firebaseUser, currentTeam]);

	const handleDiscover = async (agent: Agent) => {
		if (!firebaseUser) return;
		setDiscovering(true);

		try {
			const headers = await getHeaders();
			const response = await fetch(`${API_URL}/api/v1/instances/discover`, {
				method: 'POST',
				headers: headers,
				body: JSON.stringify({ agent_id: agent.agent_id }),
			});

			if (response.ok) {
				// Discovery command queued, instances will be updated via polling
				console.log('Discovery command queued');
			} else {
				console.error('Failed to trigger discovery');
			}
		} catch (error) {
			console.error('Error triggering discovery:', error);
		} finally {
			setDiscovering(false);
		}
	};

	const handleLifecycleAction = async (instanceId: string, action: 'start' | 'stop' | 'restart') => {
		if (!firebaseUser) return;
		setActionLoading(`${instanceId}-${action}`);
		setShowLogs(true);

		try {
			const headers = await getHeaders();
			const response = await fetch(`${API_URL}/api/v1/instances/${instanceId}/${action}`, {
				method: 'POST',
				headers: headers,
			});

			const responseData = await response.json();
			console.log(`${action} response:`, responseData);

			if (response.ok) {
				if (responseData.command_id) {
					setActiveCommandId(responseData.command_id);
				}
				console.log(`${action} command queued for ${instanceId}`, responseData);
				// Keep actionLoading active - it will be cleared when the log stream completes
			} else {
				console.error(`Failed to ${action} instance:`, response.status, responseData);
				setShowLogs(false);
				setActionLoading(null);
			}
		} catch (error) {
			console.error(`Error ${action} instance:`, error);
			setShowLogs(false);
			setActionLoading(null);
		}
	};

	const handleInstall = async (agentId: string, installMode: 'clean' | 'upgrade' = 'upgrade') => {
		if (!firebaseUser) return;
		setActionLoading('install');
		setShowLogs(true);
		setShowCleanConfirm(null);
		// Ensure selectedAgent is set so LogViewer gets the agentId
		if (!selectedAgent || selectedAgent.agent_id !== agentId) {
			const agent = agents.find(a => a.agent_id === agentId);
			if (agent) setSelectedAgent(agent);
		}

		try {
			const headers = await getHeaders();
			const response = await fetch(`${API_URL}/api/v1/instances/install`, {
				method: 'POST',
				headers: headers,
				body: JSON.stringify({ agent_id: agentId, install_mode: installMode }),
			});

			if (response.ok) {
				const data = await response.json();
				if (data.command_id) {
					setActiveCommandId(data.command_id);
				}
				console.log(`Install command queued (mode: ${installMode})`);
			} else {
				console.error('Failed to queue install command');
				setShowLogs(false);
			}
		} catch (error) {
			console.error('Error installing:', error);
			setShowLogs(false);
		} finally {
			setActionLoading(null);
		}
	};

	const handleAgentUpdate = async (agent: Agent) => {
		if (!firebaseUser) return;
		setActionLoading(`update-${agent.agent_id}`);
		setSelectedAgent(agent);
		setShowLogs(true);

		try {
			const headers = await getHeaders();
			const response = await fetch(`${API_URL}/api/v1/agents/${agent.agent_id}/update`, {
				method: 'POST',
				headers: headers,
				body: JSON.stringify({ version: agent.latest_version || 'latest' }),
			});

			if (response.ok) {
				const data = await response.json();
				if (data.command_id) {
					setActiveCommandId(data.command_id);
				}
			} else {
				console.error('Failed to queue agent update');
				setShowLogs(false);
			}
		} catch (error) {
			console.error('Error updating agent:', error);
			setShowLogs(false);
		} finally {
			setActionLoading(null);
		}
	};

	const handleStopAllGateways = async (agentId: string) => {
		if (!firebaseUser) return;
		setActionLoading(`stop-all-${agentId}`);
		setShowLogs(true);
		const agent = agents.find(a => a.agent_id === agentId);
		if (agent) setSelectedAgent(agent);

		try {
			const headers = await getHeaders();
			const response = await fetch(`${API_URL}/api/v1/agents/${agentId}/stop-gateways`, {
				method: 'POST',
				headers: headers,
			});

			if (response.ok) {
				const data = await response.json();
				if (data.command_id) {
					setActiveCommandId(data.command_id);
				}
			}
		} catch (error) {
			console.error('Stop all gateways error:', error);
			setShowLogs(false);
		} finally {
			setActionLoading(null);
		}
	};

	const handleUninstallInstance = async (instanceId: string) => {
		if (!firebaseUser) return;
		setActionLoading(`uninstall-${instanceId}`);
		setShowUninstallConfirm(null);

		try {
			const headers = await getHeaders();
			const response = await fetch(`${API_URL}/api/v1/instances/${instanceId}`, {
				method: 'DELETE',
				headers: headers,
			});

			if (response.ok) {
				setBots(prev => prev.filter(b => b.instance_id !== instanceId));
			} else {
				const error = await response.json();
				console.error('Uninstall failed:', error);
			}
		} catch (error) {
			console.error('Uninstall error:', error);
		} finally {
			setActionLoading(null);
		}
	};

	const getStatusColor = (status: string) => {
		switch (status) {
			case 'running':
			case 'online':
				return 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300';
			case 'stopped':
			case 'offline':
				return 'bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300';
			case 'error':
				return 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300';
			default:
				return 'bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300';
		}
	};

	// No agent deployed yet
	if (currentStep === 'no-agent' && agents.length === 0) {
		return (
			<>
				<LicenseExpiredBanner />
				<div className="min-h-screen bg-slate-50 dark:bg-slate-950">
					<div className="p-8">
					{/* Header */}
					<div className="mb-8">
						<h1 className="font-heading text-4xl font-bold text-slate-900 dark:text-white mb-2">Workspaces</h1>
						<p className="text-slate-600 dark:text-slate-400">Isolated environments, each with their own configuration</p>
					</div>

					{/* Main deployment guide */}
					<div className="max-w-4xl">
						<div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl shadow-sm p-8">
							<div className="flex items-start gap-6 mb-8">
								<div className="flex-shrink-0 w-16 h-16 bg-gradient-to-br from-primary-500 to-secondary-600 rounded-2xl flex items-center justify-center shadow-lg">
									<svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
										<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 12h14M5 12a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v4a2 2 0 01-2 2M5 12a2 2 0 00-2 2v4a2 2 0 002 2h14a2 2 0 002-2v-4a2 2 0 00-2-2m-2-4h.01M17 16h.01" />
									</svg>
								</div>
								<div className="flex-1">
									<h2 className="font-heading text-2xl font-bold text-slate-900 dark:text-white mb-2">Quick Setup</h2>
									<p className="text-slate-600 dark:text-slate-400">The Clawdgate connector runs alongside your AI bots (like OpenClaw) to monitor for prompt injection, enforce security policies, and give you remote control. Setup takes about 2 minutes.</p>
								</div>
							</div>

							<PlatformInstallGuide
								registrationToken={registrationToken}
								tokenLoading={tokenLoading}
								tokenError={tokenError}
							/>

							{/* Help section */}
							<div className="mt-8 pt-8 border-t border-slate-200 dark:border-slate-800">
								<div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-xl p-6">
									<div className="flex items-start gap-3">
										<svg className="w-6 h-6 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
											<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
										</svg>
										<div>
											<p className="text-sm font-semibold text-blue-900 dark:text-blue-300 mb-2">Need help?</p>
											<p className="text-sm text-blue-800 dark:text-blue-400">
												The connector is a lightweight process that runs on your machine alongside your AI bots. It scans prompts for injection attacks, enforces your security policies, and connects to this dashboard for remote management. Your credentials and data never leave your machine.
											</p>
										</div>
									</div>
								</div>
							</div>

						</div>
					</div>
				</div>
			</div>
			</>
		);
	}

	// Agent deployed and ready
	return (
		<>
			<LicenseExpiredBanner />
			<div className="min-h-screen bg-slate-50 dark:bg-slate-950">
				<div className="p-8">
				{/* Header */}
				<div className="mb-8">
					<h1 className="font-heading text-4xl font-bold text-slate-900 dark:text-white mb-2">Workspaces</h1>
					<p className="text-slate-600 dark:text-slate-400">Isolated environments, each with their own configuration</p>
				</div>

				{/* Bot Limit Warning */}
				<LimitWarning type="bot" />

				{/* Connected Agents */}
				<div className="mb-8">
					<h2 className="font-heading text-xl font-bold text-slate-900 dark:text-white mb-4">Connected Devices</h2>
					<div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
						{agents.map((agent) => (
							<div
								key={agent.agent_id}
								className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl shadow-sm p-6 hover:shadow-md transition-shadow cursor-pointer"
								onClick={() => setSelectedAgent(agent)}
							>
								<div className="flex items-start justify-between mb-4">
									<div className="flex items-center gap-3">
										<div className="w-12 h-12 bg-gradient-to-br from-primary-500 to-secondary-600 rounded-xl flex items-center justify-center shadow-lg">
											<svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
												<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 12h14M5 12a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v4a2 2 0 01-2 2M5 12a2 2 0 00-2 2v4a2 2 0 002 2h14a2 2 0 002-2v-4a2 2 0 00-2-2m-2-4h.01M17 16h.01" />
											</svg>
										</div>
										<div>
											<h3 className="font-semibold text-slate-900 dark:text-white">{agent.hostname}</h3>
											<p className="text-xs text-slate-500 dark:text-slate-400">{agent.platform} • v{agent.version}</p>
										</div>
									</div>
									<div className="flex items-center gap-2">
										{agent.update_available && (
											<span className="px-2 py-0.5 text-xs font-semibold rounded-full bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300">
												v{agent.latest_version}
											</span>
										)}
										<span className={`px-3 py-1 text-xs font-semibold rounded-full ${getStatusColor(agent.status)}`}>
											{agent.status}
										</span>
									</div>
								</div>
								<div className="flex items-center justify-between text-sm">
									<span className="text-slate-600 dark:text-slate-400">
										{bots.filter(b => b.agent_id === agent.agent_id).length} workspace(s)
									</span>
									<div className="flex gap-2">
										{agent.update_available && agent.status === 'online' && (
											<button
												onClick={(e) => {
													e.stopPropagation();
													handleAgentUpdate(agent);
												}}
												disabled={actionLoading === `update-${agent.agent_id}`}
												className="px-3 py-2 bg-amber-500 hover:bg-amber-600 text-white rounded-lg font-medium transition-colors cursor-pointer disabled:opacity-50 text-xs"
											>
												{actionLoading === `update-${agent.agent_id}` ? 'Updating...' : 'Update Connector'}
											</button>
										)}
										<button
											onClick={(e) => {
												e.stopPropagation();
												handleStopAllGateways(agent.agent_id);
											}}
											disabled={actionLoading === `stop-all-${agent.agent_id}` || agent.status !== 'online'}
											className="px-3 py-2 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg font-medium transition-colors cursor-pointer disabled:opacity-50 text-xs"
											title="Stop any running clawdbot/openclaw gateway processes"
										>
											{actionLoading === `stop-all-${agent.agent_id}` ? 'Stopping...' : 'Stop All Bots'}
										</button>
										<button
											onClick={(e) => {
												e.stopPropagation();
												handleDiscover(agent);
											}}
											disabled={discovering}
											className="px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white rounded-lg font-medium transition-colors cursor-pointer disabled:opacity-50 text-xs"
										>
											{discovering ? 'Scanning...' : 'Scan for Bots'}
										</button>
									</div>
								</div>
							</div>
						))}
					</div>
				</div>

				{/* Bot Instances */}
				{bots.length > 0 && (
					<div>
						<h2 className="font-heading text-xl font-bold text-slate-900 dark:text-white mb-4">Workspaces</h2>
						<div className="space-y-4">
							{bots.map((bot) => (
								<div
									key={bot.instance_id}
									className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl shadow-sm p-6 hover:shadow-md transition-shadow"
								>
									<div className="flex items-start justify-between">
										<div className="flex-1">
											<div className="flex items-center gap-3 mb-2">
												<h3 className="font-heading text-xl font-bold text-slate-900 dark:text-white">
													Workspace
												</h3>
												<span className={`px-3 py-1 text-xs font-semibold rounded-full ${getStatusColor(bot.running_status)}`}>
													{bot.running_status}
												</span>
											</div>
											<div className="space-y-1 text-sm text-slate-600 dark:text-slate-400">
												<p>
													<span className="font-medium">Version:</span> {bot.clawdbot_version || 'Unknown'}
												</p>
												<p>
													<span className="font-medium">Path:</span> {bot.install_path}
												</p>
												<p>
													<span className="font-medium">Device:</span> {bot.agent_hostname || agents.find(a => a.agent_id === bot.agent_id)?.hostname || bot.agent_id}
												</p>
											</div>
											{bot.status_warning && (
												<div className="flex items-center gap-2 mt-2 px-3 py-2 rounded-lg bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800/40 text-xs text-amber-700 dark:text-amber-400">
													<svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
														<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
													</svg>
													<span>{bot.status_warning}</span>
												</div>
											)}
										</div>

										<div className="flex flex-col gap-2 items-end">
											{bot.running_status === 'error' && (
												<button
													onClick={() => handleLifecycleAction(bot.instance_id, 'stop')}
													disabled={!!actionLoading}
													className="px-3 py-1.5 text-xs font-medium bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors cursor-pointer disabled:opacity-50 flex items-center gap-1.5"
												>
													{actionLoading === `${bot.instance_id}-stop` ? (
														<svg className="w-3.5 h-3.5 animate-spin" fill="none" viewBox="0 0 24 24">
															<circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
															<path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
														</svg>
													) : (
														<svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
															<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
															<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 10a1 1 0 011-1h4a1 1 0 011 1v4a1 1 0 01-1 1h-4a1 1 0 01-1-1v-4z" />
														</svg>
													)}
													Force Stop
												</button>
											)}
											<div className="flex gap-2">
											<button
												onClick={() => handleLifecycleAction(bot.instance_id, 'start')}
												disabled={!!actionLoading}
												className="p-2 text-slate-600 dark:text-slate-400 hover:text-primary-600 dark:hover:text-primary-400 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg transition-colors cursor-pointer disabled:opacity-50"
												title="Start"
											>
												{actionLoading === `${bot.instance_id}-start` ? (
													<svg className="w-5 h-5 animate-spin" fill="none" viewBox="0 0 24 24">
														<circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
														<path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
													</svg>
												) : (
													<svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
														<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
														<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
													</svg>
												)}
											</button>
											<button
												onClick={() => handleLifecycleAction(bot.instance_id, 'stop')}
												disabled={!!actionLoading}
												className="p-2 text-slate-600 dark:text-slate-400 hover:text-yellow-600 dark:hover:text-yellow-400 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg transition-colors cursor-pointer disabled:opacity-50"
												title="Stop"
											>
												{actionLoading === `${bot.instance_id}-stop` ? (
													<svg className="w-5 h-5 animate-spin" fill="none" viewBox="0 0 24 24">
														<circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
														<path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
													</svg>
												) : (
													<svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
														<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
														<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 10a1 1 0 011-1h4a1 1 0 011 1v4a1 1 0 01-1 1h-4a1 1 0 01-1-1v-4z" />
													</svg>
												)}
											</button>
											<button
												onClick={() => handleLifecycleAction(bot.instance_id, 'restart')}
												disabled={!!actionLoading}
												className="p-2 text-slate-600 dark:text-slate-400 hover:text-blue-600 dark:hover:text-blue-400 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg transition-colors cursor-pointer disabled:opacity-50"
												title="Restart"
											>
												{actionLoading === `${bot.instance_id}-restart` ? (
													<svg className="w-5 h-5 animate-spin" fill="none" viewBox="0 0 24 24">
														<circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
														<path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
													</svg>
												) : (
													<svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
														<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
													</svg>
												)}
											</button>
											<button
												onClick={() => setShowUninstallConfirm(bot.instance_id)}
												disabled={actionLoading === `uninstall-${bot.instance_id}`}
												className="p-2 text-slate-600 dark:text-slate-400 hover:text-red-600 dark:hover:text-red-400 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg transition-colors cursor-pointer disabled:opacity-50"
												title="Remove workspace"
											>
												<svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
													<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
												</svg>
											</button>
										</div>
										<div className="flex gap-2">
											<button
												onClick={() => handleInstall(bot.agent_id, 'upgrade')}
												disabled={actionLoading === 'install'}
												className="px-3 py-1.5 text-xs font-medium bg-primary-600 hover:bg-primary-700 text-white rounded-lg transition-colors cursor-pointer disabled:opacity-50"
												title="Upgrade: preserves configuration and tokens"
											>
												{actionLoading === 'install' ? 'Installing...' : 'Upgrade'}
											</button>
											<button
												onClick={() => setShowCleanConfirm(bot.agent_id)}
												disabled={actionLoading === 'install'}
												className="px-3 py-1.5 text-xs font-medium bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors cursor-pointer disabled:opacity-50"
												title="Fresh Install: removes all config and data"
											>
												Fresh Install
											</button>
										</div>
									</div>
								</div>
								</div>
							))}
						</div>
					</div>
				)}

				{/* Install new instance (no bots on selected agent) */}
				{selectedAgent && bots.filter(b => b.agent_id === selectedAgent.agent_id).length === 0 && !discovering && (
					<div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl shadow-sm p-8">
						<h3 className="font-heading text-xl font-bold text-slate-900 dark:text-white mb-4">No workspaces found</h3>
						<p className="text-slate-600 dark:text-slate-400 mb-4">
							Install Bot on <strong>{selectedAgent.hostname}</strong>, or use Fresh Install if there's an existing installation to replace.
						</p>
						<div className="bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-800 rounded-xl p-4 mb-6">
							<div className="flex items-start gap-3">
								<svg className="w-5 h-5 text-purple-600 dark:text-purple-400 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
									<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
								</svg>
								<p className="text-sm text-purple-800 dark:text-purple-400">
									<strong>Recommended:</strong> When installing Bot, skip the channel configuration step (Telegram, Slack, Discord, etc.). Clawdgate will securely proxy all IM traffic with threat detection and policy enforcement — configure channels later from the Integrations tab.
								</p>
							</div>
						</div>
						<div className="flex gap-3">
							<button
								onClick={() => handleInstall(selectedAgent.agent_id)}
								disabled={actionLoading === 'install'}
								className="px-6 py-3 bg-gradient-to-r from-primary-600 to-secondary-600 hover:from-primary-700 hover:to-secondary-700 text-white rounded-xl font-semibold shadow-lg hover:shadow-xl transition-all duration-200 cursor-pointer disabled:opacity-50"
								title="Install or upgrade: preserves existing configuration if present"
							>
								<svg className="inline w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
									<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
								</svg>
								{actionLoading === 'install' ? 'Installing...' : 'Install Bot'}
							</button>
							<button
								onClick={() => setShowCleanConfirm(selectedAgent.agent_id)}
								disabled={actionLoading === 'install'}
								className="px-6 py-3 bg-red-600 hover:bg-red-700 text-white rounded-xl font-semibold shadow-lg hover:shadow-xl transition-all duration-200 cursor-pointer disabled:opacity-50"
								title="Fresh Install: removes all existing config and data, then installs fresh"
							>
								<svg className="inline w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
									<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
								</svg>
								{actionLoading === 'install' ? 'Installing...' : 'Fresh Install'}
							</button>
						</div>
					</div>
				)}

				{/* Uninstall Instance Confirmation Dialog */}
				{showUninstallConfirm && (
					<div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
						<div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl shadow-xl p-8 max-w-md mx-4">
							<div className="flex items-center gap-3 mb-4">
								<div className="w-12 h-12 bg-red-100 dark:bg-red-900/30 rounded-full flex items-center justify-center">
									<svg className="w-6 h-6 text-red-600 dark:text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
										<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
									</svg>
								</div>
								<h3 className="font-heading text-xl font-bold text-slate-900 dark:text-white">Remove Workspace</h3>
							</div>
							<p className="text-slate-600 dark:text-slate-400 mb-6">
								This will <strong>stop the running gateway</strong> and remove the workspace record, including its policies and credentials. The installed files on the machine will remain.
							</p>
							<div className="flex gap-3 justify-end">
								<button
									onClick={() => setShowUninstallConfirm(null)}
									className="px-4 py-2 bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-700 rounded-lg font-medium transition-colors cursor-pointer"
								>
									Cancel
								</button>
								<button
									onClick={() => handleUninstallInstance(showUninstallConfirm)}
									disabled={actionLoading === `uninstall-${showUninstallConfirm}`}
									className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg font-semibold transition-colors cursor-pointer disabled:opacity-50"
								>
									{actionLoading === `uninstall-${showUninstallConfirm}` ? 'Removing...' : 'Yes, Remove'}
								</button>
							</div>
						</div>
					</div>
				)}

				{/* Clean Install Confirmation Dialog */}
				{showCleanConfirm && (
					<div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
						<div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl shadow-xl p-8 max-w-md mx-4">
							<div className="flex items-center gap-3 mb-4">
								<div className="w-12 h-12 bg-red-100 dark:bg-red-900/30 rounded-full flex items-center justify-center">
									<svg className="w-6 h-6 text-red-600 dark:text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
										<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
									</svg>
								</div>
								<h3 className="font-heading text-xl font-bold text-slate-900 dark:text-white">Fresh Install</h3>
							</div>
							<p className="text-slate-600 dark:text-slate-400 mb-6">
								This will <strong>remove all bot configuration and data</strong>, including gateway tokens, channel settings, and credentials. A fresh configuration will be generated. Are you sure?
							</p>
							<div className="flex gap-3 justify-end">
								<button
									onClick={() => setShowCleanConfirm(null)}
									className="px-4 py-2 bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-700 rounded-lg font-medium transition-colors cursor-pointer"
								>
									Cancel
								</button>
								<button
									onClick={() => handleInstall(showCleanConfirm, 'clean')}
									disabled={actionLoading === 'install'}
									className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg font-semibold transition-colors cursor-pointer disabled:opacity-50"
								>
									{actionLoading === 'install' ? 'Installing...' : 'Yes, Fresh Install'}
								</button>
							</div>
						</div>
					</div>
				)}

				{/* Live Logs Viewer */}
				{showLogs && selectedAgent && (
					<div className="mt-6">
						<LogViewer
							agentId={selectedAgent.agent_id}
							commandId={activeCommandId}
							isActive={showLogs}
							onClose={() => {
								setShowLogs(false);
								setActiveCommandId(null);
								setActionLoading(null);
							}}
							onComplete={() => {
								setActionLoading(null);
							}}
						/>
					</div>
				)}
			</div>
		</div>
		</>
	);
}
